const DEFAULT_CHUNK_SIZE = 20 * 1024 * 1024; // 20MB

interface UploadChunkArgs {
  uploadUrl: string;
  blob: Blob;
  startByte: number;
  endByte: number;
  signal?: AbortSignal;
}

const uploadChunk = async ({
  uploadUrl,
  blob,
  startByte,
  endByte,
  signal,
}: UploadChunkArgs) => {
  const chunk = blob.slice(startByte, endByte);
  const res = await fetch(uploadUrl, {
    method: "PUT",
    headers: {
      "Content-Type": blob.type,
      "Content-Length": chunk.size.toString(),
      "Content-Range": `bytes ${startByte}-${endByte - 1}/${blob.size}`,
    },
    body: chunk,
    signal,
  });

  if (!res.ok && res.status !== 308) {
    throw new Error("Failed to upload chunk");
  }

  return res.status;
};

interface UploadSingleChunkArgs {
  uploadUrl: string;
  contentType: string;
  blob: Blob;
  signal?: AbortSignal;
}

const uploadSingleChunk = async ({
  uploadUrl,
  contentType,
  blob,
  signal,
}: UploadSingleChunkArgs) => {
  const res = await fetch(uploadUrl, {
    method: "PUT",
    headers: {
      "Content-Type": contentType,
      "Content-Length": blob.size.toString(),
    },
    body: blob,
    signal,
  });

  if (!res.ok && res.status !== 308) {
    throw new Error("Failed to upload chunk");
  }

  return res.status;
};

type UploadFileArgs = {
  signedUrl: string;
  contentType: string;
  blob: Blob;
  chunkSize?: number;
  onProgress?: (progress: { percent: number }) => void;
  cacheControl?: string;
  signal?: AbortSignal;
};

export const uploadFileToUrl = async ({
  signedUrl,
  contentType,
  blob,
  chunkSize = DEFAULT_CHUNK_SIZE,
  onProgress,
  cacheControl = "max-age=0, must-revalidate",
  signal,
}: UploadFileArgs): Promise<{ url: string; size: number }> => {
  const headers: Record<string, string> = {
    "x-goog-resumable": "start",
    "Content-Length": "0",
    "Content-Type": contentType,
    "x-goog-content-length-range": `0,${blob.size.toString()}`,
  };
  if (cacheControl) {
    headers["Cache-Control"] = cacheControl;
  }
  const resumableSession = await fetch(signedUrl, {
    method: "POST",
    headers,
    signal,
  });

  const uploadUrl = resumableSession.headers.get("Location");
  if (!uploadUrl) {
    throw new Error("Failed to get upload URL from resumable session");
  }

  if (blob.size <= chunkSize) {
    await uploadSingleChunk({
      uploadUrl,
      contentType,
      blob,
      signal,
    });
  } else {
    for (let startByte = 0; startByte < blob.size; startByte += chunkSize) {
      const endByte = Math.min(startByte + chunkSize, blob.size);
      await uploadChunk({ uploadUrl, blob, startByte, endByte, signal });
      const progressPercent = Math.round((endByte / blob.size) * 100);
      onProgress?.({ percent: progressPercent });
    }
  }

  const urlMatch = signedUrl.match(/[^?]+/);
  return {
    size: blob.size,
    url: urlMatch && urlMatch.length > 0 ? urlMatch[0] : signedUrl,
  };
};
